import "./SectionDrawerView.less";

import { notification, Button, Drawer, Empty, Icon, Input, Spin } from "antd";
import * as React from "react";

import { SectionApi, SectionVersionApi } from "lib/api";
import { CancellationToken } from "lib/api/base";
import { IBaseProps } from "lib/data/interfaces";
import { Section, SectionTemplate, SectionVersion } from "lib/data/model";
import { StateService } from "lib/services";

import { DeleteButton } from "components/general/StandardButtons";
import { PublishButton } from "components/general/StandardButtons/PublishButton/PublishButton";
import { ValidationError } from "lib/data/transfer/ValidationError";

interface ISectionDrawerViewProps extends IBaseProps {
	section: Section;
	version: SectionVersion;
	visible: boolean;
	isEditable: boolean;
	onClose?: () => void;
	onNameChange?: (name: string) => void;
	onDraftRevert?: (newDraft: SectionVersion) => void;
	onDraftPublish?: (errors?: ValidationError) => void;
}

interface ISectionDrawerViewState {
	isLoading: boolean;
	isLoaded: boolean;
	isEditable: boolean;
	isEditing: boolean;
	isSaving: boolean;
	isPublishing: boolean;
	isReverting: boolean;
	draftName: string;
	draft: SectionVersion | undefined;
}

export class SectionDrawerView extends React.Component<ISectionDrawerViewProps, ISectionDrawerViewState> {

	private apiCancellationToken: CancellationToken = new CancellationToken();


	private template!: SectionTemplate;

	constructor(props: ISectionDrawerViewProps) {

		super(props);

		this.state = {
			isLoading: true,
			isLoaded: false,
			isEditable: false,
			isEditing: false,
			isSaving: false,
			isPublishing: false,
			isReverting: false,
			draft: undefined,
			draftName: this.props.version.name
		};
	}

	public async componentWillMount() {

		// Check For Section Template
		if (this.props.section.template === undefined) {

			// Get Section Template
			const template = await SectionApi.getTemplate(this.props.section, undefined);

			// Check Template
			if (template !== undefined) {

				// Store Template
				this.template = template;

				// Indicate Is NOT Loading
				StateService.setCancelableState(() => this.setState({ isLoading: false, isLoaded: true, isEditable: this.props.isEditable }), this.apiCancellationToken);
			}
			else {

				// Indicate Is NOT Loaded
				StateService.setCancelableState(() => this.setState({ isLoading: false, isLoaded: false }), this.apiCancellationToken);
			}
		}
		else {

			// Store Template
			this.template = this.props.section.template;

			// Indicate Is NOT Loading
			StateService.setCancelableState(() => this.setState({ isLoading: false, isLoaded: true, isEditable: this.props.isEditable }), this.apiCancellationToken);
		}
	}

	public async componentWillReceiveProps(newProps: ISectionDrawerViewProps) {

		// Indicate Is Loading
		StateService.setCancelableState(() => this.setState({ isLoading: true }), this.apiCancellationToken);

		// Check For Section Template
		if (newProps.section.template === undefined) {

			// Get Section Template
			const template = await SectionApi.getTemplate(newProps.section, undefined);

			// Check Template
			if (template !== undefined) {

				// Store Template
				this.template = template;

				// Indicate Is NOT Loading
				StateService.setCancelableState(() => this.setState({ isLoading: false, isLoaded: true, isEditable: newProps.isEditable }), this.apiCancellationToken);
			}
			else {

				// Indicate Is NOT Loaded
				StateService.setCancelableState(() => this.setState({ isLoading: false, isLoaded: false }), this.apiCancellationToken);
			}
		}
		else {

			// Store Template
			this.template = newProps.section.template;

			// Indicate Is NOT Loading
			StateService.setCancelableState(() => this.setState({ isLoading: false, isLoaded: true, isEditable: newProps.isEditable }), this.apiCancellationToken);
		}
	}

	public componentWillUnmount() {

		// Cancel Outstanding API Calls
		this.apiCancellationToken.cancel();
	}

	public render() {

		// Initialize Data Field Value
		let itemComponent: React.ReactNode;

		// Check Component State
		if (this.state.isLoading) {

			itemComponent = (
				<Spin />
			);
		}
		else if (!this.state.isLoaded) {

			itemComponent = (
				<Empty
					image={<Icon type="exclamation-circle" />}
					description="Unable To Load"
				/>
			);
		}
		else if (this.state.isEditing) {

			itemComponent = (
				<React.Fragment>
					<Input
						placeholder={this.props.version.name ? this.props.version.name : "Enter a section name"}
						onChange={this.onNameValueChange}
						defaultValue={this.props.version.name}
					/>
					{this.getSecondaryComponent()}
				</React.Fragment>
			);
		}
		else {
			itemComponent = (
				<React.Fragment>
					<h4>{this.props.version.name}</h4>
					{this.getSecondaryComponent()}
				</React.Fragment>
			);
		}

		return (
			<Drawer
				placement="right"
				closable={true}
				onClose={this.props.onClose}
				visible={this.props.visible}
			>
				{itemComponent}

			</Drawer>
		);
	}

	private getSecondaryComponent = (): React.ReactNode => {

		// Initialize Actions
		const actions: Array<React.ReactElement<Button>> = [];
		let draftActions: React.ReactNode;

		if (this.state.isEditable) {

			// Check Current State (For Actions)
			if (!this.state.isEditing) {

				actions.push(
					(
						<Button.Group key={actions.length}>

							<Button
								type="ghost"
								icon="edit"
								onClick={this.toggleNameEditing}
							/>
						</Button.Group>
					)
				);
			}
			else {

				actions.push(
					(
						<Button.Group key={actions.length}>
							<Button
								type="ghost"
								icon="check"
								onClick={this.saveVersionName}
								loading={this.state.isSaving}
							/>
							<Button
								type="ghost"
								icon="close"
								onClick={this.toggleNameEditing}
								disabled={this.state.isSaving}
							/>
						</Button.Group>
					)
				);
			}

			draftActions = (
				<section className="section-version-action-buttons">
					<PublishButton actionText="Publish&nbsp;Draft" onPublish={this.publishDraft} isLoading={this.state.isPublishing} isDisabled={this.state.isReverting} />
					<DeleteButton actionText="Delete&nbsp;Changes" onClick={this.revertDraft} isLoading={this.state.isReverting} isDisabled={this.state.isPublishing} />
				</section>
			);
		}

		return (
			<React.Fragment>
				{actions}
				<p>{(this.template !== undefined ? this.template.name : "Unknown")}</p>
				{draftActions}
				{/* <section>
					Draft: {this.props.section.draft ? this.props.section.draft.name : <i>No current draft available</i>}
				</section>
				Published: {this.props.section.published ? <section>{this.props.section.published.metadata.map((metaItem, index) => <p key={index}>{metaItem.id}</p>)}</section> : null}
				Superceded: {this.props.section.superceded ? <section>{this.props.section.superceded.map((sectionVersion, index) => <p key={index}>{sectionVersion.name}</p>)}</section> : null} */}
			</React.Fragment>
		);
	}

	private toggleNameEditing = () => {

		// Trigger Editing
		StateService.setCancelableState(() => this.setState({ isEditing: !this.state.isEditing }), this.apiCancellationToken);
	}

	private saveVersionName = () => {

		// Indicate Is Saving
		StateService.setCancelableState(() => this.setState({ isSaving: true }), this.apiCancellationToken);

		// Build New Section Version
		const newVersion = new SectionVersion(this.state.draftName);

		// Configure New Section Version
		newVersion.id = this.props.version.id;
		newVersion.state = this.props.version.state;

		// Save Version Name
		const updatedVersion = SectionVersionApi.updateDraft(this.props.section, newVersion);

		// Check Version
		if (updatedVersion !== undefined) {

			// Inform User
			notification.success({
				message: "Save Successful",
				description: "The draft's name was successfully changed."
			});

			// Toggle Name Editing Mode (Turn Off)
			this.toggleNameEditing();

			// Check For Save Handler
			if (this.props.onNameChange !== undefined) {

				// Call Save Handler
				this.props.onNameChange(this.state.draftName);
			}
		}

		// Indicate Is NOT Saving
		StateService.setCancelableState(() => this.setState({ isSaving: false }), this.apiCancellationToken);
	}

	private onNameValueChange = (event: any) => {

		// Get New Value
		const { value } = event.target;

		// Update Name With New Value
		StateService.setCancelableState(() => this.setState({ draftName: value }), this.apiCancellationToken);
	}

	private publishDraft = async (isCascade: boolean) => {

		// Indicate Is Publishing
		StateService.setCancelableState(() => this.setState({ isPublishing: true }), this.apiCancellationToken);

		// Publish Draft
		const isSuccess = await SectionApi.publishDraft(this.props.section.id!, isCascade);

		// Check For Successful Publish
		if (isSuccess !== undefined) {

			if (isSuccess instanceof ValidationError) {

				// Inform User
				notification.warning({
					message: "Unable To Publish",
					description: "There were validation errors while attempting to publish.  Please correct the errors and try publishing again."
				});

				// Check For Publish Handler
				if (this.props.onDraftPublish !== undefined) {

					// Call Publish Handler
					this.props.onDraftPublish(isSuccess);
				}
			}
			else {

				if (isSuccess) {

					// Inform User
					notification.success({
						message: "Publish Successful",
						description: "The draft was published successfully."
					});
				}
				else {

					// Inform User
					notification.warning({
						message: "Publish Partially Successful",
						description: "One or more child drafts failed validation and were unable to be published."
					});
				}

				// Check For Publish Handler
				if (this.props.onDraftPublish !== undefined) {

					// Call Publish Handler
					this.props.onDraftPublish();
				}
			}
		}
		else {

			// Inform User
			notification.error({
				message: "Error Publishing"
			});
		}

		// Indicate Is NOT Publishing
		StateService.setCancelableState(() => this.setState({ isPublishing: false }), this.apiCancellationToken);
	}

	private revertDraft = async () => {

		// Indicate Is Reverting
		StateService.setCancelableState(() => this.setState({ isReverting: true }), this.apiCancellationToken);

		const newDraft = await SectionApi.revertDraft(this.props.section.id!);

		if (newDraft) {

			// Inform User
			notification.success({
				message: "Revert Successful",
				description: "All changes made to this draft have been reverted."
			});

			// Check For Revert Handler
			if (this.props.onDraftRevert !== undefined) {

				// Call Revert Handler
				this.props.onDraftRevert(newDraft);
			}
		}
		else {

			// Inform User
			notification.error({
				message: "Revert Failed",
				description: "Failed to revert the changes that have been made to this draft."
			});
		}

		// Indicate Is NOT Reverting
		StateService.setCancelableState(() => this.setState({ isReverting: false }), this.apiCancellationToken);
	}
}
